const HIGH_IMAGE_QUALITY = 0.95;
const COMPRESSIBLE_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

type DecodedImage = ImageBitmap | HTMLImageElement;

async function decodeImage(file: File): Promise<DecodedImage | null> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // A browser with createImageBitmap can still reject unsupported formats.
      return null;
    }
  }

  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return null;
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      const timeoutId = window.setTimeout(() => reject(new Error('Unable to decode image.')), 1000);
      image.onload = () => {
        window.clearTimeout(timeoutId);
        resolve(image);
      };
      image.onerror = () => {
        window.clearTimeout(timeoutId);
        reject(new Error('Unable to decode image.'));
      };
      image.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function getImageDimensions(image: DecodedImage) {
  return 'naturalWidth' in image
    ? { width: image.naturalWidth, height: image.naturalHeight }
    : { width: image.width, height: image.height };
}

function encodeCanvas(canvas: HTMLCanvasElement, mimeType: string) {
  return new Promise<Blob | null>((resolve) => {
    const callback = (blob: Blob | null) => resolve(blob);
    if (mimeType === 'image/png') {
      canvas.toBlob(callback, mimeType);
    } else {
      canvas.toBlob(callback, mimeType, HIGH_IMAGE_QUALITY);
    }
  });
}

/** Re-encodes supported raster images only when the result is smaller. */
export async function compressImageForUpload(file: File) {
  const mimeType = file.type.toLowerCase();
  if (!file.size || !COMPRESSIBLE_IMAGE_TYPES.has(mimeType)) {
    return file;
  }

  let decodedImage: DecodedImage | null = null;
  try {
    decodedImage = await decodeImage(file);
    if (!decodedImage) {
      return file;
    }

    const { width, height } = getImageDimensions(decodedImage);
    if (width <= 0 || height <= 0) {
      return file;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }

    context.drawImage(decodedImage, 0, 0, width, height);
    const compressedBlob = await encodeCanvas(canvas, mimeType);
    if (!compressedBlob || compressedBlob.size >= file.size) {
      return file;
    }

    return new File([compressedBlob], file.name, {
      type: compressedBlob.type || mimeType,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  } finally {
    if (decodedImage && 'close' in decodedImage) {
      decodedImage.close();
    }
  }
}
